// Zoekresultaten op de kaart, op beide kaarten hetzelfde: bolletjes zolang de
// kaart is uitgezoomd, rood oplichtende percelen zodra alle percelen in de
// tiles zitten. MapLibre wisselt zelf via maxzoom/minzoom; hier geen zoomlogica.
//
// De drempel volgt uit de tiles: combined_*.pmtiles loopt tot z14 en is met
// `--drop-densest-as-needed` gebouwd, dus onder 14 ontbreken kleine percelen
// (z10: 206 van 360, z13: 17, z14: 0). Vanaf 14 wordt de z14-tegel overzoomd
// en is elk perceel er. De bolletjes vervangen dus de percelen die nog niet
// getekend kunnen worden.

import { FillLayerSpecification } from "maplibre-gl";
import { circleSearchStyle, parcelMatchStyle } from "./layerStyles";
import { PARCEL_SOURCE_LAYER } from "./pmtiles";

export const PARCEL_COMPLETE_ZOOM = 14;

export const searchDotsLayer = {
  ...circleSearchStyle,
  maxzoom: PARCEL_COMPLETE_ZOOM,
};

// Welke percelen oplichten staat in feature-state (`match`), niet in een
// filter met een namenlijst: een `in`-filter is een lineaire scan per feature
// per tile en een dorpskern op zoom 14 telt al bijna 1.800 treffers.
// Feature-state is O(1), blijft bewaard voor tiles die later laden, en hover
// werkt op dezelfde bron al zo.
export const searchMatchLayer = {
  minzoom: PARCEL_COMPLETE_ZOOM,
  paint: {
    "fill-color": [
      "case",
      ["boolean", ["feature-state", "match"], false],
      parcelMatchStyle.paint!["fill-color"],
      "transparent",
    ],
    "fill-outline-color": [
      "case",
      ["boolean", ["feature-state", "match"], false],
      parcelMatchStyle.paint!["fill-outline-color"],
      "transparent",
    ],
  } as FillLayerSpecification["paint"],
};

/** Zet de `match`-state op precies `names`; wat eerder gezet was en er niet
 * meer bij hoort, gaat uit. Het tile-id is de perceelnaam ("GST00 I 1"), dus
 * niet `objectID`. Geeft terug wat nu gezet staat, voor de volgende aanroep. */
export function applyMatches(
  map: {
    setFeatureState(target: FeatureTarget, state: Record<string, boolean>): unknown;
    removeFeatureState(target: FeatureTarget, key?: string): unknown;
  },
  source: string,
  previous: Iterable<string>,
  names: Iterable<string>
): Set<string> {
  const next = new Set(names);
  for (const name of previous) {
    if (!next.has(name)) {
      map.removeFeatureState({ source, sourceLayer: PARCEL_SOURCE_LAYER, id: name }, "match");
    }
  }
  for (const name of next) {
    map.setFeatureState({ source, sourceLayer: PARCEL_SOURCE_LAYER, id: name }, { match: true });
  }
  return next;
}

type FeatureTarget = { source: string; sourceLayer?: string; id: string };

// De index kent per perceel alleen de centroid (geen geometrie, om ruimte te
// sparen). Een perceel waarvan de centroid net buiten beeld ligt, maar dat
// zelf wel in beeld is, zou dan niet oplichten. De zoekbbox is daarom aan
// elke kant een tiende breder dan het scherm.
export const BOUNDS_MARGIN = 0.1;

/** `[north, east, south, west]` van `bounds`, aan elke kant `margin` breder. */
export function widenedBounds(
  bounds: { getNorth(): number; getEast(): number; getSouth(): number; getWest(): number },
  margin = BOUNDS_MARGIN
): [number, number, number, number] {
  const dLat = (bounds.getNorth() - bounds.getSouth()) * margin;
  const dLng = (bounds.getEast() - bounds.getWest()) * margin;
  return [
    bounds.getNorth() + dLat,
    bounds.getEast() + dLng,
    bounds.getSouth() - dLat,
    bounds.getWest() - dLng,
  ];
}

export const hitNames = (features: GeoJSON.FeatureCollection): string[] =>
  features.features.map((f) => f.properties?.name).filter((name): name is string => !!name);
